// === Staff details — pay settings, payroll categories and identity documents ===
//
// Side data for a staff member (a `profiles` row). Kept off profiles because
// profiles is tenant identity and never client-writable; the API writes these
// tables with the service key. See supabase/migrations/20261004_staff_payroll_documents.sql.

import { z } from 'zod';

export const STAFF_DOCUMENT_TYPES = ['licence', 'passport', 'temporary_visa', 'citizenship'] as const;
export type StaffDocumentType = (typeof STAFF_DOCUMENT_TYPES)[number];
export const StaffDocumentTypeSchema = z.enum(STAFF_DOCUMENT_TYPES);

export const STAFF_DOCUMENT_LABELS: Record<StaffDocumentType, string> = {
  licence: 'Licence',
  passport: 'Passport',
  temporary_visa: 'Temporary Visa',
  citizenship: 'Citizenship',
};

/** Blank means "not set" — sent as null, never coerced to 0. */
const hours = (max: number) => z.number().min(0).max(max).nullable();

export const StaffDetailsUpdateSchema = z.object({
  start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD').nullable(),
  hours_per_week: hours(168),
  base_hours: hours(24),
  standard_hours: hours(24),
  /** Full / part time rostered shiftworker — afternoon/night shift loading instead of the day span rule. Omitted = unchanged. */
  shiftworker: z.boolean().optional(),
  payroll_category_ids: z.array(z.number().int().positive()),
});

export const StaffDocumentsSyncSchema = z.object({
  /** The full list for this document type. Stored files not in it are removed. */
  files: z.array(z.object({ file_path: z.string().min(1).max(500) })).max(50),
});

export type StaffDetailsUpdate = z.infer<typeof StaffDetailsUpdateSchema>;
export type StaffDocumentsSync = z.infer<typeof StaffDocumentsSyncSchema>;

// === Payroll categories per job title (admin Program Settings → Titles) ===
// The standard categories every company is seeded with, keyed by
// payroll_category.kind. An admin title (user_titles_backends) lists the kinds
// that apply to it plus the award/NES wording for each; a company job title
// with the same name then shows only those categories, with that wording.

export const PAYROLL_CATEGORY_KINDS = [
  'base_hours',
  'overtime_15',
  'overtime_2',
  'casual_overtime',
  'shift_loading',
  'night_loading',
  'public_holiday',
  'annual_leave',
  'sick_leave',
  'compassionate_leave',
  'unpaid_leave',
  'lunch',
  'training',
] as const;
export type PayrollCategoryKind = (typeof PAYROLL_CATEGORY_KINDS)[number];

export const PAYROLL_CATEGORY_LABELS: Record<PayrollCategoryKind, string> = {
  base_hours: 'Base Hours',
  overtime_15: 'Overtime x 1.5',
  overtime_2: 'Overtime x 2',
  casual_overtime: 'Casual Overtime',
  shift_loading: 'Shift Loading',
  night_loading: 'Casual Night Loading',
  public_holiday: 'Public Holidays',
  annual_leave: 'Annual Leave',
  sick_leave: 'Sick Pay',
  compassionate_leave: 'Compassionate Leave',
  unpaid_leave: 'Unpaid Leave',
  lunch: 'Lunch',
  training: 'Training',
};

export const TitlePayrollCategorySchema = z.object({
  kind: z.enum(PAYROLL_CATEGORY_KINDS),
  detail: z.string().max(2000),
});

/** null = the title sets no categories, so the company chooses from all of them. */
export const TitlePayrollCategoriesSchema = z.array(TitlePayrollCategorySchema).max(PAYROLL_CATEGORY_KINDS.length).nullable();

export type TitlePayrollCategory = z.infer<typeof TitlePayrollCategorySchema>;

// === Pay rule for a job title ===
// The admin title's payroll categories plus its fixed pay settings. Anything
// set here is read-only on the Staff dialog for staff on a same-named job
// title; a null setting stays editable per person (e.g. a part-timer's agreed
// hours per week).

export const TITLE_PAY_HOURS_FIELDS = ['hours_per_week', 'base_hours', 'standard_hours'] as const;
export type TitlePayHoursField = (typeof TITLE_PAY_HOURS_FIELDS)[number];

export const TitlePayHoursSchema = z.object({
  hours_per_week: hours(168),
  base_hours: hours(24),
  standard_hours: hours(24),
});

export interface TitlePayRule {
  /** null = the title sets no categories; the company chooses. */
  readonly categories: readonly TitlePayrollCategory[] | null;
  readonly hours_per_week: number | null;
  readonly base_hours: number | null;
  readonly standard_hours: number | null;
  /** Pay Help tab text with {placeholders}; null = no tab. */
  readonly pay_help: string | null;
}

/** NUMERIC comes back from PostgREST as a string. */
function toHours(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * A user_titles_backends row → its pay rule, or null when it sets nothing.
 * Bad category data is ignored rather than trusted.
 */
export function parseTitlePayRule(row: {
  payroll_categories: unknown;
  hours_per_week?: unknown;
  base_hours?: unknown;
  standard_hours?: unknown;
  pay_help?: unknown;
}): TitlePayRule | null {
  const parsed = TitlePayrollCategoriesSchema.safeParse(row.payroll_categories ?? null);
  const categories = parsed.success && parsed.data !== null && parsed.data.length > 0 ? parsed.data : null;
  const rule: TitlePayRule = {
    categories,
    hours_per_week: toHours(row.hours_per_week),
    base_hours: toHours(row.base_hours),
    standard_hours: toHours(row.standard_hours),
    pay_help: typeof row.pay_help === 'string' && row.pay_help.trim() !== '' ? row.pay_help : null,
  };
  const setsHours = TITLE_PAY_HOURS_FIELDS.some((f) => rule[f] !== null);
  return categories === null && !setsHours && rule.pay_help === null ? null : rule;
}

/** Title names are matched trimmed and case-insensitive, like the licence exemption. */
export const titleKey = (title: string | null | undefined): string => (title ?? '').trim().toUpperCase();

// === Pay Help placeholders ===
// The admin writes the Pay Help text; these are filled with the staff
// member's own pay settings. Missing values show "—", never a stand-in number.

export const PAY_HELP_PLACEHOLDERS: ReadonlyArray<{ key: string; meaning: string }> = [
  { key: 'title', meaning: 'Job title name' },
  { key: 'hours_per_week', meaning: 'Hours per week' },
  { key: 'base_hours', meaning: 'Base hours' },
  { key: 'standard_hours', meaning: 'Standard hours' },
  { key: 'hours_per_day', meaning: 'Hours per week ÷ 5' },
  { key: 'difference', meaning: 'Base − standard hours (when base is longer)' },
  { key: 'rdo_days', meaning: 'Standard ÷ difference — days to build one RDO' },
  { key: 'hours_8_weeks', meaning: 'Hours per week × 8' },
  { key: 'ordinary_start', meaning: "Company's ordinary hours start (Settings → Pay), e.g. 6:00 am" },
  { key: 'ordinary_finish', meaning: "Company's ordinary hours finish (Settings → Pay), e.g. 6:00 pm" },
];

const MISSING = '—';
const fmtHours = (n: number): string => String(Math.round(n * 100) / 100);

export function fillPayHelp(
  text: string,
  v: {
    title: string; hours_per_week: number | null; base_hours: number | null; standard_hours: number | null;
    /** Company ordinary-hours span, e.g. '6:00 am' — Settings → Pay. */
    ordinary_start?: string | null; ordinary_finish?: string | null;
  },
): string {
  const { hours_per_week: week, base_hours: base, standard_hours: standard } = v;
  const gap = base !== null && standard !== null && base > standard ? base - standard : null;
  const values: Record<string, string> = {
    title: v.title,
    hours_per_week: week === null ? MISSING : fmtHours(week),
    base_hours: base === null ? MISSING : fmtHours(base),
    standard_hours: standard === null ? MISSING : fmtHours(standard),
    hours_per_day: week === null ? MISSING : fmtHours(week / 5),
    difference: gap === null ? MISSING : fmtHours(gap),
    rdo_days: gap === null || standard === null ? MISSING : fmtHours(standard / gap),
    hours_8_weeks: week === null ? MISSING : fmtHours(week * 8),
    ordinary_start: v.ordinary_start ?? MISSING,
    ordinary_finish: v.ordinary_finish ?? MISSING,
  };
  // Unknown {words} are left as typed so a typo is visible, not blanked.
  return text.replace(/\{([a-z0-9_]+)\}/g, (whole, key: string) => values[key] ?? whole);
}
